import React, { useState } from "react";
import { useProject, DEFAULT_ADJ } from "../state/projectStore";
import { dpiStatus } from "../lib/dpiCheck";
import { pxToMmAtDPI } from "../lib/units";
import {
  Maximize2,
  Minimize2,
  Image as ImageIcon,
  AlertTriangle,
  Copy,
  Crosshair,
  RotateCcw,
  Ruler,
} from "lucide-react";

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
      {label}
    </label>
    {children}
  </div>
);

const SmallBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex-1 h-8 text-xs rounded-sm border transition-colors ${
      active
        ? "bg-[#00E5FF] text-black border-[#00E5FF] font-medium"
        : "bg-[#252A30] text-[#9CA3AF] border-[#31373E] hover:text-[#F3F4F6]"
    }`}
  >
    {children}
  </button>
);

const NumRow = ({ label, value, onChange, step = 0.5, suffix, testid }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] w-16">{label}</span>
    <input
      type="number"
      value={value}
      step={step}
      onChange={(e) => {
        const v = parseFloat(e.target.value);
        if (!Number.isNaN(v)) onChange(v);
      }}
      data-testid={testid}
      className="h-8 flex-1 bg-[#121417] border border-[#31373E] text-sm px-2 rounded-sm font-mono text-[#F3F4F6] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
    />
    {suffix && <span className="text-[10px] font-mono text-[#6B7280] w-4">{suffix}</span>}
  </div>
);

export default function RightSidebar() {
  const { state, updateSlot, applyToAll, selectSlot, setPiece, fillSheet } = useProject();
  const selected = state.selectedSlot;
  const adj = selected != null ? state.slotAdjustments[selected] || DEFAULT_ADJ : null;
  const image = adj?.imageId ? state.images.find((i) => i.id === adj.imageId) : null;

  const [targetDPI, setTargetDPI] = useState(300);

  let dpi = null;
  if (image) {
    dpi = dpiStatus(image.width_px, image.height_px, state.piece.width_mm, state.piece.height_mm);
  }

  // When changing fit, automatically center & reset zoom for a clean baseline.
  const changeFit = (mode) => {
    updateSlot(selected, { fit: mode, offset_x: 0, offset_y: 0, zoom: 1 });
  };

  const centerImage = () => updateSlot(selected, { offset_x: 0, offset_y: 0 });
  const resetFraming = () =>
    updateSlot(selected, { fit: "fill", zoom: 1, offset_x: 0, offset_y: 0, rotation: 0, distort: false, scaleX: 1, scaleY: 1 });

  // Real-size at chosen DPI: physical mm of the image.
  const realSizeAtDPI = image
    ? {
        w: Math.round(pxToMmAtDPI(image.width_px, targetDPI) * 100) / 100,
        h: Math.round(pxToMmAtDPI(image.height_px, targetDPI) * 100) / 100,
      }
    : null;

  return (
    <aside
      className="w-[280px] border-l border-[#31373E] bg-[#1C1F24] flex flex-col shrink-0 overflow-y-auto"
      data-testid="right-sidebar"
    >
      <div className="px-4 py-3 border-b border-[#31373E] bg-[#181B20]">
        <h2 className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#6B7280]">
          Propiedades
        </h2>
        <p className="text-sm mt-1 text-[#F3F4F6]">
          {selected != null ? `Pieza #${selected + 1}` : "Sin selección"}
        </p>
      </div>

      {selected == null ? (
        <div className="p-4 text-xs text-[#6B7280] leading-relaxed">
          Haz clic sobre una pieza del canvas para editar su imagen, encuadre, zoom y rotación. Arrastra con el ratón sobre la imagen para moverla dentro del marco.
          <div className="mt-3 px-3 py-2 border border-[#31373E] bg-[#121417] rounded-sm">
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Plantilla</div>
            <div className="text-[10px] font-mono text-[#9CA3AF]">
              Hoja: {state.page.width_mm} × {state.page.height_mm} mm
              <br />
              Pieza: {state.piece.width_mm} × {state.piece.height_mm} mm
              <br />
              Sangrado: {state.bleed} mm
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 flex flex-col gap-4">
          {/* Imagen actual */}
          <Field label="Imagen">
            {image ? (
              <div className="border border-[#31373E] bg-[#121417] rounded-sm p-2 flex items-center gap-2">
                <img src={image.dataUrl} alt={image.name} className="w-10 h-10 object-cover rounded-sm border border-[#31373E]" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#F3F4F6] truncate">{image.name}</div>
                  <div className="text-[10px] font-mono text-[#6B7280]">
                    {image.width_px} × {image.height_px} px
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-[#31373E] bg-[#121417] rounded-sm p-3 text-center text-xs text-[#6B7280]">
                <ImageIcon size={16} className="mx-auto mb-1" />
                Sin imagen
              </div>
            )}
          </Field>

          {/* DPI status */}
          {dpi && (
            <div
              className={`px-3 py-2 rounded-sm border text-[10px] font-mono flex items-center gap-2 ${
                dpi.level === "ok"
                  ? "bg-[#1C2A1F] border-[#2D5A36] text-[#7FD992]"
                  : dpi.level === "warn"
                  ? "bg-[#2A2418] border-[#5A4720] text-[#FFB300]"
                  : "bg-[#2A1818] border-[#5A2020] text-[#FF3B30]"
              }`}
              data-testid="dpi-indicator"
            >
              {dpi.level !== "ok" && <AlertTriangle size={12} />}
              DPI efectivo: <span className="font-bold">{dpi.dpi}</span>
              {dpi.level !== "ok" && <span>· baja resolución</span>}
            </div>
          )}

          {/* Encuadre */}
          <Field label="Encuadre">
            <div className="flex gap-1">
              <SmallBtn active={adj.fit === "fill"} onClick={() => changeFit("fill")} testid="fit-fill-btn">
                <Maximize2 size={12} className="inline mr-1" />
                Rellenar
              </SmallBtn>
              <SmallBtn active={adj.fit === "fit"} onClick={() => changeFit("fit")} testid="fit-fit-btn">
                <Minimize2 size={12} className="inline mr-1" />
                Encajar
              </SmallBtn>
            </div>
            <div className="flex gap-1 mt-1">
              <button
                onClick={centerImage}
                data-testid="center-image-btn"
                className="flex-1 h-7 text-[10px] rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6] flex items-center justify-center gap-1"
                title="Centrar imagen dentro del marco"
              >
                <Crosshair size={11} /> Centrar
              </button>
              <button
                onClick={resetFraming}
                data-testid="reset-framing-btn"
                className="flex-1 h-7 text-[10px] rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6] flex items-center justify-center gap-1"
                title="Restablecer todo el encuadre"
              >
                <RotateCcw size={11} /> Reset encuadre
              </button>
            </div>
          </Field>

          {/* Zoom (proporcional) — solo si distort está desactivado */}
          {!adj.distort && (
            <Field label={`Zoom (proporcional) · ${Math.round(adj.zoom * 100)}%`}>
              <input
                type="range"
                min="0.1"
                max="4"
                step="0.05"
                value={adj.zoom}
                onChange={(e) => updateSlot(selected, { zoom: parseFloat(e.target.value) })}
                data-testid="zoom-slider"
                className="accent-[#00E5FF] w-full"
              />
              <div className="flex gap-1 mt-1">
                <button onClick={() => updateSlot(selected, { zoom: Math.max(0.1, adj.zoom - 0.05) })} className="flex-1 h-6 text-[10px] rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6]" data-testid="zoom-dec-btn">−</button>
                <button onClick={() => updateSlot(selected, { zoom: 1 })} className="flex-1 h-6 text-[10px] rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6]" data-testid="zoom-reset-btn">100%</button>
                <button onClick={() => updateSlot(selected, { zoom: Math.min(4, adj.zoom + 0.05) })} className="flex-1 h-6 text-[10px] rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6]" data-testid="zoom-inc-btn">+</button>
              </div>
            </Field>
          )}

          {/* Distorsión opcional */}
          <Field label="Distorsión (X/Y separados)">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!adj.distort}
                onChange={(e) => updateSlot(selected, { distort: e.target.checked })}
                data-testid="distort-toggle"
                className="accent-[#FFB300]"
              />
              <span className="text-xs text-[#F3F4F6]">Permitir escala no proporcional</span>
            </label>
            {adj.distort && (
              <div className="mt-2 flex flex-col gap-2">
                <div>
                  <div className="text-[10px] font-mono text-[#9CA3AF] mb-1">Escala X · {Math.round((adj.scaleX || 1) * 100)}%</div>
                  <input
                    type="range"
                    min="0.1"
                    max="4"
                    step="0.05"
                    value={adj.scaleX || 1}
                    onChange={(e) => updateSlot(selected, { scaleX: parseFloat(e.target.value) })}
                    data-testid="scale-x-slider"
                    className="accent-[#FFB300] w-full"
                  />
                </div>
                <div>
                  <div className="text-[10px] font-mono text-[#9CA3AF] mb-1">Escala Y · {Math.round((adj.scaleY || 1) * 100)}%</div>
                  <input
                    type="range"
                    min="0.1"
                    max="4"
                    step="0.05"
                    value={adj.scaleY || 1}
                    onChange={(e) => updateSlot(selected, { scaleY: parseFloat(e.target.value) })}
                    data-testid="scale-y-slider"
                    className="accent-[#FFB300] w-full"
                  />
                </div>
              </div>
            )}
          </Field>

          {/* Desplazamiento */}
          <Field label="Desplazamiento (X/Y mm)">
            <span className="text-[10px] text-[#6B7280]">Tip: arrastra la imagen sobre el marco para moverla.</span>
            <NumRow label="X" value={adj.offset_x} onChange={(v) => updateSlot(selected, { offset_x: v })} suffix="mm" testid="offset-x-input" />
            <NumRow label="Y" value={adj.offset_y} onChange={(v) => updateSlot(selected, { offset_y: v })} suffix="mm" testid="offset-y-input" />
          </Field>

          {/* Rotación */}
          <Field label="Rotación">
            <NumRow label="Grados" value={adj.rotation} onChange={(v) => updateSlot(selected, { rotation: v })} step={1} suffix="°" testid="rotation-input" />
            <div className="flex gap-1 mt-1">
              <button onClick={() => updateSlot(selected, { rotation: 0 })} data-testid="rotate-0-btn" className="flex-1 h-7 text-[10px] rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6]">0°</button>
              <button onClick={() => updateSlot(selected, { rotation: 90 })} data-testid="rotate-90-btn" className="flex-1 h-7 text-[10px] rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6]">90°</button>
              <button onClick={() => updateSlot(selected, { rotation: 180 })} data-testid="rotate-180-btn" className="flex-1 h-7 text-[10px] rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6]">180°</button>
              <button onClick={() => updateSlot(selected, { rotation: 270 })} data-testid="rotate-270-btn" className="flex-1 h-7 text-[10px] rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6]">270°</button>
            </div>
          </Field>

          {/* Tamaño real según DPI */}
          {image && (
            <Field label="Tamaño real según DPI">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={targetDPI}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!Number.isNaN(v) && v > 0) setTargetDPI(v);
                  }}
                  data-testid="target-dpi-input"
                  min="1"
                  step="1"
                  className="h-8 w-20 bg-[#121417] border border-[#31373E] text-sm px-2 rounded-sm font-mono text-[#F3F4F6] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
                />
                <span className="text-[10px] font-mono text-[#9CA3AF]">dpi</span>
                {realSizeAtDPI && (
                  <span className="ml-auto text-[10px] font-mono text-[#F3F4F6]">
                    = {realSizeAtDPI.w} × {realSizeAtDPI.h} mm
                  </span>
                )}
              </div>
              <div className="flex gap-1 mt-1">
                <button
                  onClick={() => {
                    if (!realSizeAtDPI) return;
                    setPiece({ id: "custom", label: "Personalizado", width_mm: realSizeAtDPI.w, height_mm: realSizeAtDPI.h });
                  }}
                  data-testid="fit-piece-to-dpi-btn"
                  className="flex-1 h-7 text-[10px] rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#F3F4F6] flex items-center justify-center gap-1"
                  title="Ajusta la pieza al tamaño físico que tendría la imagen a este DPI"
                >
                  <Ruler size={11} /> Ajustar pieza
                </button>
                <button
                  onClick={() => {
                    if (!realSizeAtDPI) return;
                    setPiece({ id: "custom", label: "Personalizado", width_mm: realSizeAtDPI.w, height_mm: realSizeAtDPI.h });
                    setTimeout(() => fillSheet(), 0);
                  }}
                  data-testid="fill-sheet-real-dpi-btn"
                  className="flex-1 h-7 text-[10px] rounded-sm bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium flex items-center justify-center gap-1"
                  title="Ajusta la pieza al tamaño real y rellena la hoja"
                >
                  <Ruler size={11} /> Rellenar a real
                </button>
              </div>
            </Field>
          )}

          {/* Acciones */}
          <div className="flex flex-col gap-2 pt-2 border-t border-[#31373E]">
            <button
              onClick={() =>
                applyToAll({
                  fit: adj.fit,
                  zoom: adj.zoom,
                  offset_x: adj.offset_x,
                  offset_y: adj.offset_y,
                  rotation: adj.rotation,
                  distort: adj.distort,
                  scaleX: adj.scaleX,
                  scaleY: adj.scaleY,
                })
              }
              data-testid="apply-to-all-btn"
              className="h-8 text-xs rounded-sm bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium flex items-center justify-center gap-2"
            >
              <Copy size={12} /> Aplicar ajustes a todas
            </button>
            <button
              onClick={() => selectSlot(null)}
              data-testid="deselect-btn"
              className="h-8 text-xs rounded-sm bg-transparent border border-[#31373E] text-[#6B7280] hover:text-[#F3F4F6]"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
